/**
 * /dashboard/settings/skills — Settings → People & HR → Skill catalog
 * (SOP §8 + §11.8).
 *
 * Skills are seeded EMPTY for every tenant. This page surfaces:
 *  - "Add skill" form dialog (HR-admin)
 *  - Per-row Edit / Archive controls (HR-admin)
 *  - "Include inactive" filter to re-surface archived rows (HR-admin)
 *  - "Import from common templates" bulk affordance (Engineering / Sales /
 *    Operations / HR)
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): direct port of
 * frontend/src/app/(dashboard)/dashboard/settings/skills/page.tsx.
 * Individual CRUD added in G13 skills-crud wave.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { toApiError } from "@/lib/api";
import { listSkills } from "@/lib/api/settings";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { SkillsImportButton } from "@/components/settings/skills-import-button";
import { AddSkillDialog, SkillCatalogList } from "@/components/settings/skills-crud";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/skills",
)({
  component: SettingsSkillsRoute,
});

const SKILLS_KEY = ["skills", "catalog"] as const;
const SKILLS_INACTIVE_KEY = ["skills", "catalog", "include-inactive"] as const;

function SettingsSkillsRoute() {
  const { isHrAdmin } = useHrRoles();
  const [includeInactive, setIncludeInactive] = useState(false);

  const queryKey = includeInactive ? SKILLS_INACTIVE_KEY : SKILLS_KEY;

  const q = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      return listSkills({ includeInactive: isHrAdmin && includeInactive }, signal);
    },
    staleTime: 60 * 1000,
  });

  const rows = q.data?.items ?? [];
  const invalidateKeys = [SKILLS_KEY, SKILLS_INACTIVE_KEY] as const;

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Skill Catalog</h2>
              <p className="text-sm text-muted-foreground">
                Every company has different skills, so we seed this empty.
                Pick a starter template below or add skills as you go.
              </p>
            </div>
            {isHrAdmin && (
              <AddSkillDialog invalidateKeys={invalidateKeys} />
            )}
          </div>

          {!isHrAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can manage the skill
                catalog.
              </AlertDescription>
            </Alert>
          )}

          {isHrAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-4 w-4 text-primary" />
                  Import from Template
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <SkillsImportButton
                  template="engineering"
                  invalidateKeys={[SKILLS_KEY, SKILLS_INACTIVE_KEY]}
                />
                <SkillsImportButton
                  template="sales"
                  invalidateKeys={[SKILLS_KEY, SKILLS_INACTIVE_KEY]}
                />
                <SkillsImportButton
                  template="operations"
                  invalidateKeys={[SKILLS_KEY, SKILLS_INACTIVE_KEY]}
                />
                <SkillsImportButton
                  template="hr"
                  invalidateKeys={[SKILLS_KEY, SKILLS_INACTIVE_KEY]}
                />
              </CardContent>
            </Card>
          )}

          {q.isLoading && (
            <div className="space-y-2">
              {["s1", "s2", "s3"].map((k) => (
                <Skeleton key={k} className="h-10 w-full" />
              ))}
            </div>
          )}

          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load skills</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {q.data && (
            <SkillCatalogList
              skills={rows}
              isHrAdmin={isHrAdmin}
              includeInactive={includeInactive}
              onIncludeInactiveChange={(v) => {
                setIncludeInactive(v);
              }}
              invalidateKeys={invalidateKeys}
            />
          )}
        </div>
      </SettingsShell>
    </>
  );
}
